import * as React from 'react';
import { type KeyboardEvent, type ReactNode, useEffect } from 'react';

import { use$ } from '@legendapp/state/react';
import { Breakpoints } from '@volkswagen-onehub/components-core';
import styled, { css, keyframes } from 'styled-components';

import translate from '../../utils/translate';

import { fullscreenStore, zoomStoreActions } from '../../store';
import { testId } from '../../test-utils/constants';

interface Props {
	children: ReactNode;
}

const MediaArea = ({ children }: Props) => {
	const isFullscreenMode = use$(fullscreenStore.isFullscreenMode);

	useEffect(() => {
		const handleMouseDown = (event: MouseEvent) => {
			const target = event.target as HTMLElement | null;
			if (!target || target.closest('button, a, input, select, textarea, [tabindex]')) return;

			const activeElement = document.activeElement as HTMLElement | null;
			activeElement?.blur();
		};

		document.addEventListener('mousedown', handleMouseDown);
		return () => document.removeEventListener('mousedown', handleMouseDown);
	}, []);

	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (event.target !== event.currentTarget) return;

		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			zoomStoreActions.toggleOneStepZoom();
		}
	};

	return (
		<StyledMediaArea
			data-testid={testId('Media_Area')}
			role="button"
			aria-label={translate('global.ariaLabel.zoomCarImage')}
			tabIndex={0}
			$isFullscreenMode={isFullscreenMode}
			onKeyDown={handleKeyDown}
		>
			{children}
		</StyledMediaArea>
	);
};

const visFadeIn = keyframes`
	from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
`;

const StyledMediaArea = styled.div<{ $isFullscreenMode: boolean }>`
	position: relative;
	display: grid;
	grid-template-columns: repeat(24, 1fr);
	grid-template-rows: repeat(24, 1fr);

	overflow: hidden;
	border-radius: ${({ $isFullscreenMode }) => ($isFullscreenMode ? '0' : '20px')};

	flex: 1 0 0;
	min-height: 0;

	-webkit-tap-highlight-color: transparent;

	animation: ${visFadeIn} 0.3s ease backwards;

	&:focus-visible {
		outline: 3px solid #c88c03;
	}

	${({ $isFullscreenMode }) =>
		!$isFullscreenMode &&
		css`
			@media (max-width: ${Breakpoints.b960 - 1}px) {
				aspect-ratio: 4 / 3;
				height: auto;
				flex: none;
			}
		`}
`;

export default MediaArea;
